import * as styles from './Typography.module.css'

import { getNodeText } from 'src/utils/getNodeText'
import { createElement, useMemo } from 'react'
import { kebabCase } from 'lodash-es'

type AsProps<Tag extends keyof HTMLElementTagNameMap> = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLElementTagNameMap[Tag]>,
  HTMLElementTagNameMap[Tag]
> & {
  as?: Tag
}

type LabelProps = React.DetailedHTMLProps<
  React.LabelHTMLAttributes<HTMLLabelElement>,
  HTMLLabelElement
> & { as?: 'label' }

type DefaultElementProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> & {
  as?: 'div'
}

type Props =
  | DefaultElementProps
  | AsProps<'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'>
  | AsProps<'p'>
  | AsProps<'blockquote'>
  | AsProps<'pre'>
  | AsProps<'code'>
  | LabelProps

export const PrimaryHeading: React.FC<Props> = (props) => {
  const { as = 'h1', children, ...elementProps } = props

  const slug = useMemo(() => {
    const text = getNodeText(children)
    return typeof text === 'string' ? kebabCase(text) : null
  }, [children])

  return createElement(
    as,
    {
      className: styles.primaryHeading,
      id: slug ?? undefined,
      ...elementProps,
    },
    children,
  )
}

export const SecondaryHeading: React.FC<Props> = (props) => {
  const { as = 'h2', children, ...elementProps } = props

  const slug = useMemo(() => {
    const text = getNodeText(children)
    return typeof text === 'string' ? kebabCase(text) : null
  }, [children])

  return createElement(
    as,
    {
      className: styles.secondaryHeading,
      id: slug ?? undefined,
      ...elementProps,
    },
    children,
  )
}

export const TertiaryHeading: React.FC<Props> = (props) => {
  const { as = 'h3', children, ...elementProps } = props

  const slug = useMemo(() => {
    const text = getNodeText(children)
    return typeof text === 'string' ? kebabCase(text) : null
  }, [children])

  return createElement(
    as,
    {
      className: styles.tertiaryHeading,
      id: slug ?? undefined,
      ...elementProps,
    },

    children,
  )
}

export const QuaternaryHeading: React.FC<Props> = (props) => {
  const { as = 'h4', children, ...elementProps } = props

  const slug = useMemo(() => {
    const text = getNodeText(children)
    return typeof text === 'string' ? kebabCase(text) : null
  }, [children])

  return createElement(
    as,
    {
      className: styles.quaternaryHeading,
      id: slug ?? undefined,
      ...elementProps,
    },
    children,
  )
}

export const Paragraph: React.FC<Props> = (props) => {
  const { as = 'p', children, ...elementProps } = props

  return createElement(
    as,
    {
      className: styles.paragraph,
      ...elementProps,
    },
    children,
  )
}

export const UnorderedList: React.FC<Props> = (props) => {
  const { as = 'ul', children, ...elementProps } = props

  return createElement(
    as,
    {
      className: styles.unorderedList,
      ...elementProps,
    },
    children,
  )
}

export const OrderedList: React.FC<Props> = (props) => {
  const { as = 'ol', children, ...elementProps } = props

  return createElement(
    as,
    {
      className: styles.orderedList,
      ...elementProps,
    },
    children,
  )
}

export const PreformattedText: React.FC<Props> = (props) => {
  const { as = 'pre', children, ...elementProps } = props

  return createElement(
    as,
    {
      className: styles.preformattedText,
      ...elementProps,
    },
    children,
  )
}

export const Code: React.FC<Props> = (props) => {
  const { as = 'code', children, ...elementProps } = props

  return createElement(
    as,
    {
      className: styles.code,
      ...elementProps,
    },
    children,
  )
}

export const Caption: React.FC<Props> = (props) => {
  const { as = 'div', children, ...elementProps } = props

  return createElement(
    as,
    {
      className: styles.caption,
      ...elementProps,
    },
    children,
  )
}
